<template>
    <div class="md:ml-6">
        <div class="flex items-start font-semibold text-gray-600">
            {{ enrollmentType.singular }}<span class="text-red-500">*</span>
            <span
                v-if="
                    (enrollment.data?.type === 'new' &&
                        customerOptions.length > 0) ||
                    (enrollment.data?.type === 'existing' && showAddByBookingId)
                "
                class="px-2"
            >
                &mdash;
                <prime-button
                    link
                    class="!px-2 !py-0"
                    :label="t('purchaseContract.selectExistingParticipant')"
                    @click="handleSelectCustomer(null)"
                />
            </span>
        </div>

        <div class="flex flex-col items-center gap-2 md:flex-row">
            <template v-if="enrollment.data?.type !== 'new'">
                <enrollment-add-by-booking-id
                    v-if="showAddByBookingId"
                    v-model:visible="showAddByBookingId"
                    :show-errors="showErrors"
                    :course-id="courseId"
                    @add-enrollment="handleUpdateByBookingId"
                    @back="handleSelectCustomer(null)"
                />

                <prime-dropdown
                    v-else
                    :model-value="selectedOption"
                    :options="customerOptions"
                    option-label="commaName"
                    option-disabled="disabled"
                    :placeholder="
                        t('purchaseContract.selectFromExistingHousehold')
                    "
                    show-clear
                    class="md:w-[25rem]"
                    :class="dropdownClass"
                    :pt="{
                        item: ({ context }) => ({
                            class: [
                                'cursor-pointer font-normal overflow-hidden relative whitespace-nowrap',
                                'm-0 p-3 border-0 transition-shadow duration-200 rounded-none',
                                {
                                    'text-gray-400 dark:text-white/80':
                                        !context.focused &&
                                        !context.selected &&
                                        context.disabled,
                                    'text-gray-700 hover:text-gray-700 hover:bg-gray-200 dark:text-white/80 dark:hover:bg-gray-800':
                                        !context.focused &&
                                        !context.selected &&
                                        !context.disabled,
                                    'bg-gray-300 text-gray-700 dark:text-white/80 dark:bg-gray-800/90 hover:text-gray-700 hover:bg-gray-200 dark:text-white/80 dark:hover:bg-gray-800':
                                        context.focused &&
                                        !context.selected &&
                                        !context.disabled,
                                    'bg-primary-100 text-primary-700 dark:bg-primary-400 dark:text-white/80':
                                        context.focused && context.selected,
                                    'bg-primary-50 text-primary-700 dark:bg-primary-300 dark:text-white/80':
                                        !context.focused && context.selected
                                }
                            ]
                        })
                    }"
                    :aria-label="
                        t('purchaseContract.selectEnrollmentType', {
                            enrollmentType: enrollmentType.singular
                        })
                    "
                    @update:model-value="handleSelectCustomer"
                />
            </template>

            <enrollment-new-customer
                v-if="enrollment.data?.type === 'new'"
                :enrollment="enrollment.data"
                :identifier="`${enrollmentType.id}_${sequence}`"
                :show-errors="showErrors"
                class="pt-1 md:pt-0"
                :is-guest="isGuest"
                @update:enrollment="handleUpdateNewEnrollment"
            />

            <template
                v-if="
                    enrollment.data?.type !== 'existing' &&
                    enrollment.data?.type !== 'new' &&
                    !enrollmentType.requireContractHolder &&
                    !showAddByBookingId
                "
            >
                <template v-if="!isGuest && allowNewCustomers">
                    <div
                        class="mx-6 flex flex-col items-center whitespace-nowrap text-lg font-semibold lg:mx-10 lg:flex-row"
                    >
                        {{ t('purchaseContract.or') }}
                    </div>

                    <div class="flex flex-col items-center">
                        <span
                            v-if="isAgreement"
                            class="font-medium"
                            >{{ newCustomerPrompt }}</span
                        >
                        <prime-button
                            outlined
                            icon="pi pi-plus"
                            :label="t('purchaseContract.addNewCustomer')"
                            @click="handleAddNew"
                        />
                    </div>
                </template>

                <div
                    v-if="!isAgreement && !isGuest"
                    class="mx-6 flex flex-col items-center whitespace-nowrap text-lg font-semibold lg:mx-10 lg:flex-row"
                >
                    {{ t('purchaseContract.or') }}
                </div>

                <div
                    v-if="!isAgreement && !isGuest"
                    class="flex flex-col items-center"
                >
                    <prime-button
                        outlined
                        icon="pi pi-star-fill"
                        :pt="{
                            icon: '!text-star'
                        }"
                        :pt-options="{ mergeProps: true }"
                        :label="
                            t('friendsAndFamily.addByBookingId', {
                                bookingIdName
                            })
                        "
                        @click="handleAddByBookingId"
                    />
                </div>
            </template>

            <template v-if="showDisconnectProfile">
                <div
                    class="ml-2 flex flex-col items-center text-left md:ml-6 lg:ml-10"
                >
                    <span class="text-lg font-medium">
                        {{ t('purchaseContract.dependentMinorProfilePrompt') }}
                    </span>
                    <prime-button
                        link
                        :label="t('purchaseContract.createAccountForYourself')"
                        @click="handleDisconnectProfile"
                    />
                </div>
            </template>

            <template
                v-else-if="
                    enrollmentType.requireContractHolder && viewer !== undefined
                "
            >
                <div class="ml-6 flex items-center gap-2 lg:ml-10">
                    <span class="text-lg font-medium">{{
                        t('purchaseContract.notViewer', { name: viewer.name })
                    }}</span>
                    <prime-button
                        link
                        :label="t('purchaseContract.signInToYourProfile')"
                        @click="handleSignIntoProfile"
                    />
                </div>
            </template>
        </div>

        <div
            v-if="enrollmentWarningMessage !== '' && showErrors"
            class="font-medium text-danger-600"
        >
            {{ enrollmentWarningMessage }}
        </div>

        <div
            v-if="!showErrors && enrollmentContractHolderWarningMessage !== ''"
            class="font-medium text-danger-600"
        >
            {{ enrollmentContractHolderWarningMessage }}
        </div>

        <div
            v-else-if="!showErrors && enrollmentWaitlistErrorMessage !== ''"
            class="font-medium text-danger-600"
        >
            {{ enrollmentWaitlistErrorMessage }}
        </div>

        <prime-message
            v-if="
                enrollmentWarningMessage === '' &&
                enrollmentConstraintInfo !== null
            "
            class="w-auto"
            :closable="false"
            :severity="enrollmentConstraintInfo.required ? 'error' : 'warn'"
        >
            {{ enrollmentConstraintInfo.message }}
        </prime-message>
    </div>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button'
import PrimeDropdown from 'primevue/dropdown'
import PrimeMessage from 'primevue/message'
import type {
    EnrollmentInputEnrollment,
    EnrollmentInputEnrollmentData,
    EnrollmentInputExistingEnrollment,
    EnrollmentInputNewEnrollment
} from '~/composables/enrollment-input'
import EnrollmentNewCustomer from '~/components/PurchaseContract/CustomerPicker/EnrollmentNewCustomer.vue'
import { computed, onMounted, ref, toRef, watch } from 'vue'
import type {
    EnrollmentTypeAgeEnforcementMode,
    PlanType
} from '~/resources/graphql/graphql'
import { now, parseTimeless } from '~/composables/support/localization'
import { useActiveFacility } from '~/composables/use-active-facility'
import { differenceInYears, isAfter } from 'date-fns'
import { useShowLoginModal, useViewer } from '~/composables/use-login'
import { useI18n } from 'vue-i18n'
import type { EnrollmentConstraints } from '~/components/PurchaseContract/ParticipantDetails/enrollment-constraint'
import { randomString } from '~/composables/support/util'
import EnrollmentAddByBookingId from '~/components/PurchaseContract/CustomerPicker/EnrollmentAddByBookingId.vue'
import { useBookingIdName } from '~/composables/use-org'
import { useMutation } from '@tanstack/vue-query'
import { useShowAlert } from '~/composables/use-show-alert'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import {
    SavedParticipantEligibility,
    useWaitlistParticipantEligibility
} from '~/components/PurchaseContract/ParticipantDetails/use-waitlist-participant-eligibility'

const props = defineProps<{
    enrollmentType: {
        id: string
        singular: string
        ageEnforcementMode: EnrollmentTypeAgeEnforcementMode
        ageMinimum: number
        ageMaximum: number
        requireContractHolder: boolean
    }
    sequence: number
    enrollment: EnrollmentInputEnrollment
    participantOptions: (EnrollmentInputExistingEnrollment & {
        disabled: boolean
    })[]
    enrollmentConstraints: EnrollmentConstraints
    showErrors: boolean
    isGuest: boolean
    allowNewCustomers: boolean
    planType: PlanType
    courseId: string
}>()

const emit = defineEmits<{
    (e: 'update:enrollment', payload: EnrollmentInputEnrollment): void
}>()

const bookingIdName = useBookingIdName()
const { t } = useI18n({ useScope: 'global' })
const viewer = useViewer()
const customerOptions = computed(() => {
    return props.participantOptions.map(customer => {
        if (
            props.enrollment.data !== null &&
            props.enrollment.data.type === 'existing' &&
            props.enrollment.data.id === customer.id
        ) {
            return {
                ...customer,
                disabled: false
            }
        } else {
            return customer
        }
    })
})

const selectedOption = computed(() => {
    return customerOptions.value.find(
        customer =>
            props.enrollment.data !== null &&
            props.enrollment.data.type === 'existing' &&
            props.enrollment.data.id === customer.id
    )
})

const isAgreement = computed(() => {
    return props.planType === 'AGREEMENT'
})

const newCustomerPrompt = computed(() => {
    if (isAgreement.value) {
        return t('purchaseContract.signingForSomeoneElse')
    }

    return t('purchaseContract.purchasingForSomeoneElse')
})

function handleSelectCustomer(
    customer: (EnrollmentInputExistingEnrollment & { disabled: boolean }) | null
) {
    if (customer === null) {
        showAddByBookingId.value = false
        emitUpdatedEnrollmentData(null)
    } else if (customer.disabled) {
        return
    } else {
        emitUpdatedEnrollmentData({
            type: 'existing',
            id: customer.id,
            commaName: customer.commaName,
            firstName: customer.firstName,
            middleName: customer.middleName,
            lastName: customer.lastName,
            dateOfBirth: customer.dateOfBirth
        })
    }
}

function handleAddNew() {
    showAddByBookingId.value = false
    emitUpdatedEnrollmentData({
        type: 'new',
        key: randomString(),
        firstName: '',
        lastName: '',
        middleName: '',
        dateOfBirth: '',
        householdMember: true
    })
}

function handleUpdateNewEnrollment(data: EnrollmentInputNewEnrollment) {
    emitUpdatedEnrollmentData(data)
}

const showAddByBookingId = ref(false)
function handleAddByBookingId() {
    showAddByBookingId.value = true

    // This is our placeholder while we wait to add the customer
    emitUpdatedEnrollmentData({
        type: 'existing',
        id: '',
        commaName: '',
        firstName: '',
        middleName: '',
        lastName: '',
        dateOfBirth: ''
    })
}

function handleUpdateByBookingId(data: EnrollmentInputExistingEnrollment) {
    emitUpdatedEnrollmentData(data)
    // Ensure that no race condition exists:
    // By the time the booking ID component emits,
    // We should already have the emitted customer as a saved participant option
    if (!customerOptions.value.some(option => option.id === data.id)) {
        throw new Error()
    }
}

const activeFacility = useActiveFacility()
function getAgeRestrictionWarningMessage(data: EnrollmentInputEnrollmentData) {
    // If enrollment hasn't been selected yet return nothing
    if (data === null) {
        return ''
    }

    const customerDob = parseTimeless(data.dateOfBirth)
    const customerAge = differenceInYears(
        now(activeFacility.value.timezone),
        customerDob
    )

    // For new enrollments
    if (isNaN(customerDob.getTime())) {
        return t('purchaseContract.fillOutDateOfBirth')
    } else if (isAfter(customerDob, now(activeFacility.value.timezone))) {
        return t('purchaseContract.futureDateOfBirthWarning')
    } else if (customerAge > 120) {
        return t('purchaseContract.fillOutDateOfBirth')
    }

    // If enforcing enrollment age requirements
    if (props.enrollmentType.ageEnforcementMode === 'ENFORCE_IN_CPX') {
        const enrollmentTypeAgeMinimum = props.enrollmentType.ageMinimum
        const enrollmentTypeAgeMaximum = props.enrollmentType.ageMaximum
        // If the customer age is outside the enrollment type specifications
        if (
            props.enrollmentType.ageMinimum !== 0 &&
            props.enrollmentType.ageMaximum !== 0
        ) {
            // Return our error message
            if (
                customerAge < enrollmentTypeAgeMinimum ||
                customerAge > enrollmentTypeAgeMaximum
            ) {
                return t('purchaseContract.ageInRangeRequired', {
                    minimum: enrollmentTypeAgeMinimum,
                    maximum: enrollmentTypeAgeMaximum
                })
            }
        } else if (props.enrollmentType.ageMinimum !== 0) {
            // Return our error message
            if (customerAge < enrollmentTypeAgeMinimum) {
                return t('purchaseContract.ageAtLeastRequired', {
                    minimum: enrollmentTypeAgeMinimum
                })
            }
        } else if (props.enrollmentType.ageMaximum !== 0) {
            // Return our error message
            if (customerAge > enrollmentTypeAgeMaximum) {
                return t('purchaseContract.ageLessThanRequired', {
                    maximum: enrollmentTypeAgeMaximum + 1
                })
            }
        }
    }

    return ''
}

function getEnrollmentWarningMessage(data: EnrollmentInputEnrollmentData) {
    if (props.enrollmentType.requireContractHolder && data !== null) {
        if (
            data.type === 'existing' &&
            viewer.value !== undefined &&
            data.id !== viewer.value.customerId
        ) {
            return t('purchaseContract.onlyViewerCanBeEnrollmentType', {
                viewer: viewer.value.name,
                enrollmentType: props.enrollmentType.singular
            })
        }
    }

    return ''
}

const enrollmentContractHolderWarningMessage = computed(() => {
    return getEnrollmentWarningMessage(props.enrollment.data)
})

function getEnrollmentConstraintWarningMessage(
    data: EnrollmentInputEnrollmentData
) {
    if (data === null) {
        return null
    }

    let key
    // Actual customer record
    if (data.type === 'existing') {
        key = data.id
    } else {
        key = data.key
    }

    const constraint =
        props.enrollmentConstraints.enrollmentConstraintSegmentLookup.value
            .get(props.enrollmentType.id)
            ?.get(key)

    if (constraint === undefined || constraint === true) {
        return null
    }

    const constraintInfo =
        props.enrollmentConstraints.enrollmentConstraintConfigLookup.value.get(
            props.enrollmentType.id
        )

    if (constraintInfo === undefined) {
        throw new Error()
    }

    return constraintInfo
}

const enrollmentWarningMessage = computed(() => {
    // If we are adding by booking ID return
    if (showAddByBookingId.value) {
        return ''
    }

    const message = getEnrollmentWarningMessage(props.enrollment.data)

    if (message !== '') {
        return message
    }

    // Next try the waitlist message
    const waitlistMessage = getWaitlistWarningMessage(props.enrollment.data)

    if (waitlistMessage !== '') {
        return waitlistMessage
    }

    // Fallback to age restriction warning
    return getAgeRestrictionWarningMessage(props.enrollment.data)
})

const enrollmentConstraintInfo = computed(() => {
    return getEnrollmentConstraintWarningMessage(props.enrollment.data)
})

const { getSavedParticipantEligibility } = useWaitlistParticipantEligibility()
function getWaitlistWarningMessage(data: EnrollmentInputEnrollmentData) {
    if (data === null) {
        return ''
    }

    // Not an actual customer record
    if (data.type !== 'existing') {
        return ''
    }

    // This can be empty when we are loading a booking id
    if (data.id === '') {
        return ''
    }

    const result = getSavedParticipantEligibility(data.id)

    if (result === SavedParticipantEligibility.ELIGIBLE) {
        return ''
    }

    if (result === SavedParticipantEligibility.NOT_ON_WAITLIST) {
        return t('purchaseContract.customerNotOnWaitlist')
    }

    if (result === SavedParticipantEligibility.WAITLISTED) {
        return t('purchaseContract.customerNotEligibleFromWaitlist')
    }

    if (result === SavedParticipantEligibility.CLOSED) {
        return t('purchaseContract.onlineBookingClosed')
    }

    throw new Error()
}

const enrollmentWaitlistErrorMessage = computed(() => {
    return getWaitlistWarningMessage(props.enrollment.data)
})

onMounted(() => {
    // Make sure invalid is set properly
    emitUpdatedEnrollmentData(props.enrollment.data)
})

// Watch for any changes if we are out of sync with invalid, emit a new invalid
watch(
    [toRef(props, 'enrollment'), enrollmentConstraintInfo],
    ([newEnrollment]) => {
        const { invalid } = isEnrollmentInvalid(newEnrollment.data)
        if (invalid !== newEnrollment.invalid) {
            emitUpdatedEnrollmentData(newEnrollment.data)
        }
    }
)

function isEnrollmentInvalid(data: EnrollmentInputEnrollmentData) {
    let invalid = false
    let invalidFromConstraint = false
    if (data === null) {
        invalid = true
    } else if (getEnrollmentWarningMessage(data) !== '') {
        invalid = true
    } else if (getWaitlistWarningMessage(data) !== '') {
        invalid = true
    } else if (getAgeRestrictionWarningMessage(data) !== '') {
        invalid = true
    } else if (data.type === 'new' && data.firstName.length < 1) {
        invalid = true
    } else if (data.type === 'new' && data.lastName.length < 2) {
        invalid = true
    } else {
        const constraint = getEnrollmentConstraintWarningMessage(data)
        if (constraint !== null && constraint.required) {
            invalid = true
            invalidFromConstraint = true
        }
    }

    return { invalid, invalidFromConstraint }
}

function emitUpdatedEnrollmentData(value: EnrollmentInputEnrollmentData) {
    const { invalid, invalidFromConstraint } = isEnrollmentInvalid(value)
    emit('update:enrollment', {
        data:
            value === null
                ? null
                : {
                      ...value
                  },
        invalid,
        invalidFromConstraint
    })
}

const ageOfMajorityHint = computed(() => {
    if (
        props.enrollmentType.requireContractHolder &&
        props.enrollmentType.ageEnforcementMode === 'ENFORCE_IN_CPX'
    ) {
        return props.enrollmentType.ageMinimum
    }

    return 0
})

const showLoginModal = useShowLoginModal()
async function handleSignIntoProfile() {
    const currentViewerId = viewer.value?.customerId
    const response = await showLoginModal({
        ageOfMajorityHint: ageOfMajorityHint.value
    })
    if (
        response !== undefined &&
        currentViewerId !== undefined &&
        currentViewerId !== response
    ) {
        location.reload()
    }
}

const dropdownClass = computed(() => {
    // If we have something selected, no highlighting needed
    if (selectedOption.value) {
        return ''
    }
    // Else if we have nothing selected and show error flag is on, highlight red
    else if (props.showErrors) {
        return '!border-red-500 ring ring-red-400/50'
    }
    // Else highlight yellow
    else {
        return '!border-yellow-500 ring ring-yellow-400/50'
    }
})

const showDisconnectProfile = computed(() => {
    if (
        viewer.value === undefined ||
        !props.enrollmentType.requireContractHolder ||
        props.enrollmentType.ageEnforcementMode !== 'ENFORCE_IN_CPX'
    ) {
        return false
    }

    try {
        if (
            viewer.value.dateOfBirth === null ||
            viewer.value.dateOfBirth === ''
        ) {
            return false
        }

        const now = new Date()
        // Invalid Date of Birth
        const customerDob = parseTimeless(viewer.value.dateOfBirth)
        const customerAge = differenceInYears(now, customerDob)

        return customerAge < props.enrollmentType.ageMinimum
    } catch (e) {
        return false
    }
})

const rpQuery = useRpQuery()
const { mutate, isPending } = useMutation({
    mutationFn: () =>
        rpQuery(
            graphql(`
                mutation AuthSwitchCustomer {
                    authSwitchCustomer {
                        __typename
                        ... on AuthSwitchCustomerResult {
                            token
                        }
                    }
                }
            `)
        )
})

const showAlert = useShowAlert()
async function handleDisconnectProfile() {
    // Fetch a token for the current profile we can feed to the login modal
    mutate(undefined, {
        async onSuccess(data) {
            if (
                data.authSwitchCustomer.__typename ===
                'AuthSwitchCustomerResult'
            ) {
                const currentViewerId = viewer.value?.customerId
                const response = await showLoginModal({
                    ageOfMajorityHint: ageOfMajorityHint.value,
                    switchCustomerToken: data.authSwitchCustomer.token
                })
                if (
                    response !== undefined &&
                    currentViewerId !== undefined &&
                    currentViewerId !== response
                ) {
                    location.reload()
                }
            } else {
                showAlert(
                    t('headsUp'),
                    t('purchaseContract.refreshAndTryAgain')
                )
            }
        }
    })
}
</script>
